import * as analytics from "firebase/analytics";
import { FirebaseApp, FirebaseOptions, initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getPerformance } from "firebase/performance";
import { getStorage } from "firebase/storage";

import { getFirebaseStorageGenieAssetsRoot } from "@/common/consts";
import { isDev, isLocal, isProd, isUat } from "@/common/utilities/env";

// BEGIN-NOSCAN
const devFirebaseConfig = {
  apiKey: "AIzaSyDicywv7rZCMsDLowr7tIa0GVcHsCkezs0",
  authDomain: "genie.dev.tsc.ai",
  projectId: "genfive-dev",
  storageBucket: "genfive-dev.appspot.com",
  messagingSenderId: "392418037072",
  appId: "1:392418037072:web:d1a931364dc62be9e3f055",
  measurementId: "G-VGY34Y8J8V",
} as const;

const uatFirebaseConfig = {
  apiKey: "AIzaSyBSW-gkurdcL6wRrd34ho7y8WCgLl7en-U",
  authDomain: "genie.uat.tsc.ai",
  projectId: "genfive-uat",
  storageBucket: "genfive-uat.appspot.com",
  messagingSenderId: "371778226101",
  appId: "1:371778226101:web:59e08ba4579e0a32dbbbfc",
  measurementId: "G-Y6D915423C",
} as const;

const prodFirebaseConfig = {
  apiKey: "AIzaSyDE4ixwMvKCHZkiakfhBcaD12A_FU_iJAU",
  authDomain: "genie.tsc.ai",
  projectId: "genfive-prod",
  storageBucket: "genfive-prod.appspot.com",
  messagingSenderId: "421800737625",
  appId: "1:421800737625:web:5b121fccf627a590bb6a48",
  measurementId: "G-YF5TZ5WVQH",
} as const;
// END-NOSCAN

const setupFirebasApp = (config: FirebaseOptions) => {
  const app = initializeApp(config);

  analytics.isSupported().then((isSupported) => {
    if (isSupported) {
      analytics.getAnalytics(app);
    }
  });

  return { app, config };
};

/**
 * The project for this host (common/utilities/env). An unknown host throws instead of falling
 * back on dev: backendAddress() would send it to the prod API, so a silent dev project meant
 * tokens the backend rejects (admin's own init failed on an unknown host too).
 */
const firebaseConfigForHost = (): FirebaseOptions => {
  if (isLocal() || isDev()) return devFirebaseConfig;
  if (isUat()) return uatFirebaseConfig;
  if (isProd()) return prodFirebaseConfig;
  throw new Error(
    `GEN5-5269: no Firebase project for host "${window.location.hostname}"; ` +
      "add it to common/utilities/env (isLocal/isDev/isUat/isProd)."
  );
};

const { app, config }: { app: FirebaseApp; config: FirebaseOptions } =
  setupFirebasApp(firebaseConfigForHost());

export const auth = getAuth(app);
export const db = getFirestore(app);
export const performance = !isLocal() ? getPerformance(app) : null;
export const firebaseConfig = config;
export const storage = getStorage(app);

/**
 * Images and PDFs attached to Ask Genie (GEN5-5206), in the assets bucket under
 * `genie-attachments/`, where a lifecycle rule deletes them at two days. Its own
 * instance so the shorter retry window below applies to these uploads only.
 */
export const genieAttachmentsStorage = getStorage(
  app,
  getFirebaseStorageGenieAssetsRoot()
);
// The SDK retries a stalled upload for ten minutes by default. One minute turns
// a dropped connection into a "retry" the user can act on rather than a
// progress bar that never moves.
genieAttachmentsStorage.maxUploadRetryTime = 60_000;
